import { jsx as _jsx } from "react/jsx-runtime";
import { assertUnreachableValue } from '#pplx/frontend/common/type_utils/unreachableValue.js';
import { collectProviders } from '#pplx/frontend/composer-sdk/internal/typeahead/arbitration.js';
import { framedComposerGeometry } from '#pplx/frontend/composer-sdk/styles.js';
import classNames from 'classnames';
import { useMemo, useRef, useState } from 'react';
/**
 * Bundles the Composer's typeahead wiring: providers, the input-anchored overlay,
 * and the active variant. The active variant resolves the overlay side and attached box edge.
 */
export function useComposerTypeahead({ plugins, overlayPlacement = 'below', isFramed, suggestionAttachment, }) {
    const overlayAnchorRef = useRef(null);
    const framedCommandMenuAnchorRef = useRef(null);
    const inputCommandMenuAnchorRef = useRef(null);
    const [activeVariant, setActiveVariant] = useState(null);
    const previousProviders = useRef([]);
    const providers = useMemo(() => {
        const collected = collectProviders(plugins, previousProviders.current);
        previousProviders.current = collected;
        return collected;
    }, [plugins]);
    const opensAbove = overlayPlacement === 'above' &&
        (activeVariant === 'commandMenu' || activeVariant === 'suggestDropdown');
    const insetSuggestDropdown = isFramed && suggestionAttachment === 'input' && activeVariant === 'suggestDropdown';
    const hasCommandMenuProvider = providers.some((provider) => provider.display === 'commandMenu');
    const hasFramedCommandMenu = isFramed && overlayPlacement === 'below' && hasCommandMenuProvider;
    const hasInputCommandMenu = isFramed && overlayPlacement === 'above' && hasCommandMenuProvider;
    // Resolve destination from current props so an open menu follows layout changes.
    let commandMenuAnchorRef = overlayAnchorRef;
    if (hasFramedCommandMenu)
        commandMenuAnchorRef = framedCommandMenuAnchorRef;
    else if (hasInputCommandMenu)
        commandMenuAnchorRef = inputCommandMenuAnchorRef;
    const isFramedCommandMenuOpen = hasFramedCommandMenu && activeVariant === 'commandMenu';
    const framedCommandMenuAnchor = hasFramedCommandMenu ? (_jsx("div", { ref: framedCommandMenuAnchorRef, className: classNames('empty:hidden -mx-1.5 overflow-hidden', framedComposerGeometry.commandMenuBottomRadiusClassName) })) : null;
    const inputCommandMenuAnchor = hasInputCommandMenu ? (_jsx("div", { ref: inputCommandMenuAnchorRef, className: "absolute inset-x-0 bottom-full z-20" })) : null;
    const typeaheadOverlay = useMemo(() => (_jsx("div", { ref: overlayAnchorRef, className: classNames('absolute z-20', 
        // The anchor's parent spans the editor; framed boxes bleed past it on each side, so
        // only overlays that match the full frame extend with them.
        isFramed && !insetSuggestDropdown
            ? framedComposerGeometry.frameHorizontalInsetClassName
            : 'inset-x-0', opensAbove ? 'bottom-full' : 'top-full', insetSuggestDropdown &&
            (opensAbove
                ? framedComposerGeometry.dropdownAboveOverlapClassName
                : framedComposerGeometry.dropdownBelowOverlapClassName)) })), [insetSuggestDropdown, isFramed, opensAbove]);
    let attachedDropdownPlacement;
    switch (activeVariant) {
        case 'suggestDropdown':
        case 'spotlight':
            // `opensAbove` excludes spotlight, so both attached variants share this branch.
            attachedDropdownPlacement = opensAbove ? 'top' : 'bottom';
            break;
        case 'commandMenu':
        case 'inline':
        case null:
            break;
        default:
            assertUnreachableValue(activeVariant);
    }
    return {
        providers,
        overlayAnchorRef,
        commandMenuOverlay: {
            anchorRef: commandMenuAnchorRef,
            framed: hasFramedCommandMenu,
        },
        framedCommandMenuAnchor,
        inputCommandMenuAnchor,
        isFramedCommandMenuOpen,
        onActiveVariantChange: setActiveVariant,
        typeaheadOverlay,
        attachedDropdownPlacement,
        attachedDropdownKind: activeVariant === 'spotlight' ? 'spotlight' : 'suggestions',
        isSpotlightSurface: providers.some((provider) => provider.display === 'spotlight'),
    };
}
//# sourceMappingURL=useComposerTypeahead.js.map